/**  @jsx jsx */

import { ReactNode, useState } from 'react';
import { css, jsx } from '@emotion/core';
import { IconButton, Text, Tooltip } from '@zapier/design-system';

import EditorPreview from 'app/developer-v3/components/EditorPreview';

import type { AppService } from 'app/developer-v3/types';
import { LayoutStyles } from './constants';

const Styles = {
  root: ({ isPreviewVisible }) => [
    css`
      position: relative;
      height: 100%;
      max-width: ${LayoutStyles.maxWidth};
      display: flex;
      flex: 1 auto;
    `,
    isPreviewVisible &&
      css`
        max-width: 100%;
      `,
  ],
  editor: ({ isPreviewVisible }) => [
    css`
      display: flex;
      flex: 1 auto;
      flex-direction: column;
    `,
    isPreviewVisible &&
      css`
        width: 50%;
        max-width: ${LayoutStyles.maxWidth};
      `,
  ],
  previewToggle: css`
    position: absolute;
    top: 15px;
    right: 45px;
    z-index: 8;
  `,
};

type Props = {
  children: ReactNode;
  service: AppService;
  title: string;
};

const EditorWithPreviewLayout = (props: Props) => {
  const { children, title, service } = props;

  const [isPreviewVisible, setIsPreviewVisible] = useState<boolean>(true);

  const onTogglePreview = () => setIsPreviewVisible(!isPreviewVisible);

  return (
    <main css={Styles.root({ isPreviewVisible })}>
      <section css={Styles.editor({ isPreviewVisible })}>
        {title && (
          <Text
            fontWeight={600}
            margin="25px 0px 0px 45px"
            tag="h1"
            type="Body4"
          >
            {title}
          </Text>
        )}
        {children}
      </section>
      <div css={Styles.previewToggle}>
        <Tooltip content={isPreviewVisible ? 'Hide Preview' : 'Show Preview'}>
          <IconButton
            aria-label={isPreviewVisible ? 'Hide Preview' : 'Show Preview'}
            name={isPreviewVisible ? 'formXCircle' : 'miscEyeFill'}
            onPress={onTogglePreview}
            size="small"
          />
        </Tooltip>
      </div>
      {isPreviewVisible && <EditorPreview service={service} />}
    </main>
  );
};

EditorWithPreviewLayout.defaultProps = {
  children: null,
  title: '',
};

export default EditorWithPreviewLayout;
